// WHAT: Select component for choosing roadmap modules with inline module creation
// WHY:  Provides a dropdown for module selection with ability to create new modules on-the-fly
// DOMAIN: Admin → Roadmap
// DEPENDENCIES: Tauri core API, Lucide icons
// RISKS:
//   - NOTE: Error handling for addModule is silent - errors are not displayed
//   - NOTE: Duplicate check is case-sensitive
//   - NOTE: Selected value is shown even if not in modules list (may cause confusion)

import { core } from "@tauri-apps/api";
import { PlusCircle } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Props for ModuleSelect component
 */
interface Properties {
  /** Callback when module selection changes */
  onChange: (value: string) => void;
  /** Currently selected module value */
  value: string;
}

/**
 * WHAT:
 *   Select component for choosing roadmap modules with inline creation capability.
 *
 * WHY:
 *   Provides a user-friendly way to select modules and create new ones without
 *   leaving the current form context.
 *
 * PROPS:
 *   - value: string — Currently selected module
 *   - onChange: (val: string) => void — Handler for selection changes
 *
 * BEHAVIOR:
 *   - Loads modules on mount
 *   - Shows dropdown with all available modules
 *   - Allows creating new modules inline
 *   - Automatically selects newly created module
 *   - Shows selected value even if not in modules list
 *
 * RISKS:
 *   - NOTE: addModule errors are silently ignored - add error handling
 *   - NOTE: Duplicate check is case-sensitive - "Module" and "module" are different
 *   - NOTE: Selected value shown outside modules list may indicate data inconsistency
 *
 * @param root0
 *
 * @returns {JSX.Element} Die gerenderte ModuleSelect-Komponente
 *
 * @param root0.value
 *
 * @param root0.onChange
 *
 */
export const ModuleSelect = ({ onChange, value }: Properties) => {
  // NOTE: State for available modules list
  const [modules, setModules] = useState<string[]>([]);
  // NOTE: Loading state during module fetch
  const [, setLoading] = useState(true);
  // NOTE: State for showing add module input
  const [showAdd, setShowAdd] = useState(false);
  // NOTE: Form state for new module name
  const [newModule, setNewModule] = useState("");

  // NOTE: Load modules on component mount
  useEffect(() => {
    core
      .invoke<string[]>("fetch_roadmap_modules")
      .then((list) => {
        setModules(list);
        setLoading(false);
      })
      .catch(() => {
        // NOTE: Error handling - silently ignore for now
        setLoading(false);
      });
  }, []);

  /**
   * WHAT:
   *   Creates a new module and automatically selects it.
   *
   * WHY:
   *   Allows users to create modules on-the-fly without leaving the form.
   *
   * BEHAVIOR:
   *   - Checks for duplicate module names
   *   - Inserts module into database
   *   - Updates local state
   *   - Automatically selects newly created module
   *
   * RISKS:
   *   - WARN: Errors are silently ignored - add error handling and user feedback
   *   - NOTE: Duplicate check is case-sensitive
   *
   * @param name
   *
   */
  const addModule = async (name: string) => {
    if (!modules.includes(name)) {
      try {
        await core.invoke("insert_roadmap_module", { name });
        setModules([...modules, name]);
        onChange(name);
      } catch {
        // NOTE: Error handling should be implemented - log error or show user feedback
      }
    }
  };

  return (
    <div className="space-y-2">
      <select
        className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all"
        onChange={(e) => {
          if (e.target.value === "__add__") {
            setShowAdd(true);
          } else {
            onChange(e.target.value);
          }
        }}
        value={value}
      >
        <option className="bg-slate-700 text-white" value="">
          – Select module –
        </option>
        {modules.map((module_) => (
          <option className="bg-slate-700 text-white" key={module_} value={module_}>
            {module_}
          </option>
        ))}
        {/* Show selected value even if not in modules list yet */}
        {value && !modules.includes(value) ? (
          <option className="bg-slate-700 text-white" key={value} value={value}>
            {value}
          </option>
        ) : null}
        <option className="bg-slate-700 text-white" value="__add__">
          Add new module
        </option>
      </select>

      {showAdd ? (
        <div className="flex gap-2">
          <input
            className="flex-grow px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all"
            onChange={(e) => {
              setNewModule(e.target.value);
            }}
            placeholder="Modulname"
            type="text"
            value={newModule}
          />
          <button
            className="flex items-center gap-1 text-sm text-tracq-deep-orange hover:text-tracq-light-orange transition-colors px-3 py-2 rounded-lg hover:bg-slate-700/50"
            onClick={() => {
              if (newModule.trim().length > 1) {
                void addModule(newModule.trim());
                setNewModule("");
                setShowAdd(false);
              }
            }}
            type="button"
          >
            <PlusCircle className="w-4 h-4" />
            Add
          </button>
        </div>
      ) : null}
    </div>
  );
};
